import { useEffect, useRef, useState } from 'react';

const DESKTOP_QUERY = '(min-width: 1024px)';

// jsdom has no window.matchMedia, so tests default to false (phone/tab markup).
function matchesDesktop() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(DESKTOP_QUERY).matches;
}

// Phones: labelled tabs, each panel at its own height (the carousel stretched
// every slide to the tallest one). lg: every panel dissolves into the grid.
// Tab semantics (tablist/tab/tabpanel) apply only below lg: display:contents
// no longer strips ARIA in current Chromium/Firefox, so at lg a panel kept
// as role=tabpanel would be announced as an orphaned region (no discoverable
// tablist, since the tab buttons are correctly display:none there). At lg
// each panel is instead a plain lg:contents wrapper with no role, id or
// aria-labelledby, same as the old CardCarousel (which carried no role
// either). Tracked in JS via matchMedia (same pattern as Layout.js /
// Header.js's prefers-color-scheme listener), not CSS alone, since ARIA
// attributes cannot be made responsive with Tailwind classes.
// Keyboard pattern: roving tabindex, Left/Right move and activate the
// previous/next tab (wrapping), Home/End jump to the first/last tab, focus
// follows the selection (WAI-ARIA tabs, automatic activation).
export default function MonthlyTabs({ tabs, className = '' }) {
  const [active, setActive] = useState(tabs[0]?.id);
  const [isDesktop, setIsDesktop] = useState(matchesDesktop);
  const tabRefs = useRef([]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
    };
  }, []);

  const activate = (index) => {
    setActive(tabs[index].id);
    tabRefs.current[index]?.focus();
  };

  const handleKeyDown = (event, index) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        activate((index + 1) % tabs.length);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        activate((index - 1 + tabs.length) % tabs.length);
        break;
      case 'Home':
        event.preventDefault();
        activate(0);
        break;
      case 'End':
        event.preventDefault();
        activate(tabs.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <div
        role="tablist"
        aria-label="This month"
        className="lg:hidden grid border-b border-gray-200/70 dark:border-gray-600/70"
        style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
      >
        {tabs.map((t, i) => {
          const on = t.id === active;
          return (
            <button
              key={t.id}
              ref={(el) => { tabRefs.current[i] = el; }}
              type="button"
              role="tab"
              id={`monthly-tab-${t.id}`}
              aria-selected={on}
              aria-controls={`monthly-panel-${t.id}`}
              tabIndex={on ? 0 : -1}
              onClick={() => setActive(t.id)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className={`-mb-px h-11 border-b-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
                on ? 'border-brand-600 font-semibold text-brand-700 dark:border-brand-400 dark:text-brand-300' : 'border-transparent font-medium text-gray-500 dark:text-gray-400'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div className={`mt-3 lg:mt-0 lg:grid ${className}`}>
        {tabs.map((t) => (
          isDesktop ? (
            <div key={t.id} className="lg:contents">
              {t.content}
            </div>
          ) : (
            <div
              key={t.id}
              role="tabpanel"
              id={`monthly-panel-${t.id}`}
              aria-labelledby={`monthly-tab-${t.id}`}
              tabIndex={0}
              className={`${t.id === active ? 'flex flex-col gap-4' : 'hidden'} focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 lg:contents`}
            >
              {t.content}
            </div>
          )
        ))}
      </div>
    </div>
  );
}
